import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import { AdvancedSearch } from "@/components/AdvancedSearch";
import { AuthMenu } from "@/components/AuthMenu";
import { ClassicSearch } from "@/components/ClassicSearch";
import { SearchModeToggle } from "@/components/SearchModeToggle";
import { SyntaxHelp } from "@/components/SyntaxHelp";
import { usePreferences } from "@/store/preferences";

export function LandingPage() {
  const navigate = useNavigate();
  const { searchMode } = usePreferences();

  function handleSearch(q: string) {
    const trimmed = q.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <>
      <Helmet>
        <title>e621 Animation Finder</title>
        <meta
          name="description"
          content="Self-hosted tag-search for e621 animations. Preview, blacklist, and export as GIF or MP4."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://gif-search.maxresdefault.zip/" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gif-search.maxresdefault.zip/" />
        <meta property="og:title" content="e621 Animation Finder" />
        <meta
          property="og:description"
          content="Self-hosted tag-search for e621 animations. Preview, blacklist, and export as GIF or MP4."
        />
        <meta property="og:image" content="https://gif-search.maxresdefault.zip/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="e621 Animation Finder" />
        <meta
          name="twitter:description"
          content="Self-hosted tag-search for e621 animations. Preview, blacklist, and export as GIF or MP4."
        />
        <meta name="twitter:image" content="https://gif-search.maxresdefault.zip/og-image.jpg" />

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "e621 Animation Finder",
          "url": "https://gif-search.maxresdefault.zip/",
          "description": "Self-hosted tag-search for e621 animations. Preview, blacklist, and export as GIF or MP4.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://gif-search.maxresdefault.zip/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-surface-0 flex flex-col px-6 py-6">
        <div className="w-full max-w-2xl mx-auto flex justify-end">
          <AuthMenu />
        </div>

        <div className="flex-1 flex flex-col items-center justify-center pt-6">
          <div className="w-full max-w-2xl space-y-6">
            <header className="text-center space-y-2">
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
                e621 Animation Finder
              </h1>
              <p className="text-zinc-400 text-sm sm:text-base max-w-md mx-auto">
                Self-hosted tag-search for e621 animations. Preview, blacklist,
                and export as GIF or MP4.
              </p>
            </header>

            <div className="flex justify-center">
              <SearchModeToggle />
            </div>

            {searchMode === "classic" ? (
              <>
                <ClassicSearch initialQuery="" onSearch={handleSearch} />
                <section className="pt-6 border-t border-surface-2">
                  <h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
                    Search syntax
                  </h2>
                  <SyntaxHelp compact />
                </section>
              </>
            ) : (
              <AdvancedSearch initialQuery="" onSearch={handleSearch} />
            )}

            {/* Feature highlights — both for new visitors who want to know
                what the tool actually does, and for crawler-discoverable
                keyword coverage. Kept compact so the search bar stays the
                star of the page. */}
            <section className="pt-6 border-t border-surface-2 grid sm:grid-cols-2 gap-3 text-sm">
              <Feature title="Smart tag search">
                Tag autocomplete with aliases and implications — finds posts a
                naïve search misses.
              </Feature>
              <Feature title="Virtualized result grid">
                Hover-only preview playback so a 240k-post index doesn't melt
                your laptop.
              </Feature>
              <Feature title="One-click GIF / MP4 export">
                Convert any animation with curated FFmpeg presets and copy a
                permanent share link.
              </Feature>
              <Feature title="Personal blacklist">
                Tag exclusions on top of the hard safety filter, synced to your
                account across devices.
              </Feature>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

function Feature({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface-1 border border-surface-3 rounded-lg p-3 space-y-1">
      <h3 className="font-semibold text-zinc-200">{title}</h3>
      <p className="text-zinc-500 text-xs leading-relaxed">{children}</p>
    </div>
  );
}
